import { makeStyles } from '@material-ui/core';
import { DataGrid, GridToolbar } from '@material-ui/data-grid';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarPicker } from '../../../components/ui/CalendarPicker';
import { DateCarousel } from '../../../components/ui/DateCarousel';
import { OfficeContext } from '../../../config/Context';
import { datesAreOnSameDay, dateStringsOnSameDay, from_YYYYMMDDHHMMSS, prettyDate, simpleDate, to_YYYYMMDDHHMMSS } from '../../../config/dateFormatting';
import { DATE_QUERY_PARAM } from '../../../constants/constants';
import { data_grid_bookings_columns } from '../../../constants/dataGridHeaders';
import { IServiceUserContent, IServiceUserInfo } from '../../../datasolvr/ServicesFacade';
import { useQuery } from '../../../hooks/useQuery';
import { IAdminBooking } from '../../../interface/interface';
import { FrontendApi } from '../../../planEatApi/FrontendApi';
import { planEatApi } from '../../../planEatApi/PlanEatApi';
import { IBookingContent, ISeatingContent, IUserContent, IVersionedObject } from '../../../planEatApi/PlanEatFacade';
import { EditAdminBookingModal } from '../components/EditAdminBookingModal';
import { extractErrorMessage } from '../../../components/utils/ErrorHandler';

const useStyles = makeStyles((theme: any) => ({
    container: {
        background: '#FFFFFF',
        boxShadow: '0px 4px 4px rgba(11, 37, 89, 0.1)',
        borderRadius: 8,
        padding: 30,
    },
    bookingsContainer: {
        height: 500
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        marginBottom: 20
    },
    titleRowContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: 20
    },
    date: {
        color: theme.palette.primary.main,
        "&:hover": {
            cursor: "pointer",
            textDecoration: "underline",
        }
    },
    calendar: {
        display: "flex",
        width: "calc(100% - 325px)",
        justifyContent: "flex-end"
    }
}));

interface Props {
    
}

export const AdminBookings: React.FC<Props> = ({}) => {
    const classes = useStyles()

    // Routing
    let queryParams = useQuery()

    // Entities
    const [date, setDate] = useState<Date>(new Date)
    const [booking, setBooking] = useState<IBookingContent>()
    const [bookings, setBookings] = useState<IVersionedObject<IBookingContent>[]>([]);
    const [users, setUsers] = useState<IVersionedObject<IUserContent>[]>([])
    const [serviceUsers, setServiceUsers] = useState<IServiceUserInfo[]>([]);
    const [seatings, setSeatings] = useState<IVersionedObject<ISeatingContent>[]>([]);
    const [seatingsOnDate, setSeatingsOnDate] =  useState<IVersionedObject<ISeatingContent>[]>([]);
    const [adminBookings, setAdminBookings] = useState<IAdminBooking[]>([])

    // State
    const [isCalendarPicking, setIsCalendarPicking] = useState(false)
    const [haveDateFilter, setHaveDateFilter] = useState(false)

    // Context
    const officeContext = useContext(OfficeContext);  

    /* Update local date if global different than today */
    useEffect(() => {
        let dateParam = queryParams.get(DATE_QUERY_PARAM)
        if(dateParam !== null && dateParam !== simpleDate(new Date())){
            setDate(new Date(dateParam))
            setHaveDateFilter(true)
        }
    },[])

    function getDatabase(){
        planEatApi.getBookings().then((response) => setBookings(response.data)).catch((e) => console.log(e))
        planEatApi.getSeatings().then((response) => setSeatings(response.data.filter(seating => seating.content.officeId === officeContext?.office?.content.id))).catch((e) => console.log(e))
        planEatApi.getUsers().then((response) => setUsers(response.data)).catch((e) => console.log(e))
        planEatApi.getServiceUsers().then((response) => setServiceUsers(response.data)).catch((e) => console.log(e))
    }

    useEffect(() => {
        getDatabase()
    },[officeContext])

    useEffect(() => {
        createDataRows()
    },[bookings, seatings, users, serviceUsers, date])

    function createDataRows(filterDate?: Date){
        var tmpAdminBookings: IAdminBooking[] = []

        bookings.forEach(booking => {
            let tmpSeating: IVersionedObject<ISeatingContent>[] = []
            if(filterDate || haveDateFilter){
                tmpSeating = getSeatingsOnDate()
            } else {
                tmpSeating = seatings
            }
            const seatingOfBooking = tmpSeating.find(seating => seating.content.id == booking.content.seatingId)

            if(seatingOfBooking){
                const userOfBooking = users.find(user => user.content.id === booking.content.userId)
                if(userOfBooking){
                    const serviceUserOfBooking = serviceUsers.find(serviceUser => serviceUser.content.id == userOfBooking.content.applicationUserId)
                    if(serviceUserOfBooking){
                        tmpAdminBookings.push({
                            bookingId: booking.content.id,
                            eaterName: serviceUserOfBooking?.content.id,
                            seatingName: seatingOfBooking.content.seatingName,
                            seatingStart: seatingOfBooking.content.seatingStart,
                            totalNoPeople: booking.content.bookingItems.length,
                            comment: booking.content.bookingComment
                        })
                    }
                }
            }
        })
        tmpAdminBookings = tmpAdminBookings.sort((a, b) => (from_YYYYMMDDHHMMSS(a.seatingStart) < from_YYYYMMDDHHMMSS(b.seatingStart)) ? 1 : (from_YYYYMMDDHHMMSS(a.seatingStart) > from_YYYYMMDDHHMMSS(b.seatingStart)) ? -1 : 0);
        setAdminBookings(tmpAdminBookings)
    }

    useEffect(() => {
        getSeatingsOnDate()
    },[booking])

    function getSeatingsOnDate(): IVersionedObject<ISeatingContent>[]{
        var tmpSeatings: IVersionedObject<ISeatingContent>[] = []
        var dateString = to_YYYYMMDDHHMMSS(date)
        seatings.forEach(seating => {
            if(dateStringsOnSameDay(dateString,seating.content.seatingStart)){
                tmpSeatings.push(seating)
            }
        })
        setSeatingsOnDate(tmpSeatings)
        return tmpSeatings
    }

    function editBooking(id: string){
        var tmpBooking = bookings.find(item => item.content.id === id)?.content
        setBooking(tmpBooking)
    }

    function deleteBooking(id: string){
        planEatApi.postDeleteForBooking(id)
        .then(response => {
            getDatabase()
            toast.success("Booking was deleted")
        })
        .catch(e => {
            extractErrorMessage(e, "Failed deleting booking")
            getDatabase()
        })
    }

    function updateBooking(updatedBooking: IBookingContent){
        planEatApi.postBooking(updatedBooking)
        .then(response => {
            getDatabase()
            toast.success("Booking was updated")
            setBooking(undefined)
        })
        .catch(e => {
            extractErrorMessage(e, "Failed updating booking")
            getDatabase()
        })
    }
    
    function updateDateFromBooking(bookingId: string){
        const seatingIdOfBooking = bookings.find(booking => booking.content.id === bookingId)?.content.seatingId
        const seatingStart = seatings.find(seating => seatingIdOfBooking && seating.content.id === seatingIdOfBooking)?.content.seatingStart
        if(seatingStart){
            setDate(from_YYYYMMDDHHMMSS(seatingStart))
        }
    }

    function getBookedBy(tmpBooking: IBookingContent){
        const bookedBy = adminBookings.find(item => item.bookingId === tmpBooking.id)
        return bookedBy ? bookedBy.eaterName : ""
    }

    useEffect(() => {
        if(haveDateFilter){
            createDataRows(date)
        } else {
            createDataRows()
            if(!datesAreOnSameDay(date,new Date())){
                setDate(new Date())
            }
            
        }
    },[date,haveDateFilter])

    useEffect(() => {
        if(isCalendarPicking){
            setHaveDateFilter(true)
        }
    },[isCalendarPicking, date])


    return (
        <div>
            {
                bookings &&
                <div className={classes.container}>
                    <div className={classes.titleRowContainer}>
                        <div className={classes.title}>Bookings overview</div>
                        <div>
                            {
                                haveDateFilter ?
                                <div>
                                    <div onClick={() => {setIsCalendarPicking(true)}}>{prettyDate(date,false,true)}</div>
                                    <div style={{display: "flex", columnGap: 10}}>
                                        <div className={classes.date} onClick={() => {setIsCalendarPicking(true)}}>Change filter</div>
                                        <div className={classes.date} style={{color: "black"}} onClick={() => {setHaveDateFilter(false)}}>Clear filter</div>
                                    </div>
                                   
                                </div>
                                :
                                <div>
                                    <div className={classes.date} onClick={() => setIsCalendarPicking(true)}>
                                        No date selected
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    <div className={classes.calendar}>
                        {
                            isCalendarPicking &&
                            <CalendarPicker 
                                date={date}
                                setDate={setDate}
                                isCalendarPicking={isCalendarPicking}
                                setIsCalendarPicking={setIsCalendarPicking}
                            />
                        }
                    </div>
                    <div className={classes.bookingsContainer}>
                        <DataGrid
                            rows={adminBookings}
                            columns={data_grid_bookings_columns(editBooking,deleteBooking)}
                            getRowId={(booking) => booking.bookingId}
                            pageSize={20}
                            rowsPerPageOptions={[20]}
                            components={{ Toolbar: GridToolbar }}
                            onRowClick={(row) => updateDateFromBooking(row.id as string)}
                            
                            disableColumnSelector
                            disableDensitySelector
                            componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                            }}
                        />
                    </div>
                </div>

            }
            {
                seatingsOnDate && booking && date && 
                    <EditAdminBookingModal
                        bookedBy={getBookedBy(booking)}
                        booking={booking}
                        setBooking={setBooking}
                        date={date}
                        updateBooking={updateBooking}
                        seatingsOnDate={seatingsOnDate}
                />
            }

        </div>
    )
}